import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import Layout from './layout';
import { vlsEmailsCsv, csvEmail, fromEmails, sendVlsEmail, updateEmailsData, deleteEmailsData } from './proxyurls';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';

function VlsEmails() {
  const [csvRecords, setCsvRecords] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [showSendEmailModal, setShowSendEmailModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [fromEmailsList, setFromEmailsList] = useState([]);
  const [emailFormData, setEmailFormData] = useState({
    fromEmail: '',
    subject: '',
    message: ''
  });
  const [editingRecord, setEditingRecord] = useState(null);
  const [editFormData, setEditFormData] = useState({ names: '', emails: '' });
  const [deletingRecordId, setDeletingRecordId] = useState(null);
  const [isSourceView, setIsSourceView] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [sourceCode, setSourceCode] = useState('');
  
  const editorRef = useRef(null);
  const sourceTextareaRef = useRef(null);
  const preservedTableAttributesRef = useRef({});
  const originalHtmlWithAttributesRef = useRef('');
  

  // Fetch CSV records
  const fetchCsvRecords = async () => {
    try {
      setLoading(true);
      const response = await axios.get(csvEmail);
      console.log("response.data", response.data);
      setCsvRecords(response.data.data || []);
    } catch (error) {
      console.error('Error fetching CSV records:', error);
      setErrorMessage('Error fetching CSV records');
      setTimeout(() => setErrorMessage(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCsvRecords();
  }, []);

  // Preserve table attributes when message changes (e.g., when modal opens with existing content)
  useEffect(() => {
    if (emailFormData.message && !isSourceView) {
      // Only preserve if we don't already have preserved attributes
      // This prevents overwriting when user is actively editing
      if (Object.keys(preservedTableAttributesRef.current).length === 0) {
        preservedTableAttributesRef.current = preserveTableAttributes(emailFormData.message);
        originalHtmlWithAttributesRef.current = emailFormData.message;
      }
    }
  }, [emailFormData.message, isSourceView]);

  // Handle file selection
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  // Upload CSV file
  const handleUploadCsv = async () => {
    if (!selectedFile) {
      setErrorMessage('Please select a file first');
      setTimeout(() => setErrorMessage(''), 3000);
      return;
    }

    try {
      setLoading(true);
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await axios.post(vlsEmailsCsv, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.success) {
        setSuccessMessage('CSV file uploaded successfully');
        setTimeout(() => setSuccessMessage(''), 3000);
        setSelectedFile(null);
        // Reset file input
        document.getElementById('csvFileInput').value = '';
        // Refresh records
        fetchCsvRecords();
      } else {
        setErrorMessage(response.data.message || 'Upload failed');
        setTimeout(() => setErrorMessage(''), 3000);
      }
    } catch (error) {
      console.error('Error uploading CSV:', error);
      setErrorMessage('Error uploading CSV file');
      setTimeout(() => setErrorMessage(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  // Download template (placeholder - you may need to implement this)
  const handleDownloadTemplate = () => {
    // Create a simple CSV template
    const template = 'Name,Email\nJohn Doe,john@example.com\nJane Smith,jane@example.com';
    const blob = new Blob([template], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'email_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Handle edit record - open modal
  const handleEdit = (record) => {
    console.log('Editing record:', record);
    setEditingRecord(record);
    setEditFormData({ names: record.names || '', emails: record.emails || '' });
    setShowEditModal(true);
  };

  // Handle save edit - update via API
  const handleSaveEdit = async (e) => {
    e.preventDefault();

    // Check for various possible ID field names
    const recordId = editingRecord?.emails_data_id || editingRecord?.emails_data_id || editingRecord?.ID;

    if (!editingRecord || !recordId) {
      console.error('Invalid record - no ID found:', editingRecord);
      setErrorMessage('Invalid record - ID not found');
      setTimeout(() => setErrorMessage(''), 3000);
      return;
    }

    try {
      setLoading(true);
      console.log('Updating record with ID:', recordId, 'Data:', editFormData);
      const response = await axios.put(updateEmailsData(recordId), {
        names: editFormData.names,
        emails: editFormData.emails
      });

      if (response.data.success) {
        setSuccessMessage('Record updated successfully');
        setTimeout(() => setSuccessMessage(''), 3000);
        setShowEditModal(false);
        setEditingRecord(null);
        setEditFormData({ names: '', emails: '' });
        // Refresh records
        fetchCsvRecords();
      } else {
        setErrorMessage(response.data.message || 'Failed to update record');
        setTimeout(() => setErrorMessage(''), 3000);
      }
    } catch (error) {
      console.error('Error updating record:', error);
      setErrorMessage('Error updating record');
      setTimeout(() => setErrorMessage(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  // Handle delete record - open confirmation modal
  const handleDelete = (id) => {
    console.log('Deleting record:', id);
    setDeletingRecordId(id);
    setShowDeleteModal(true);
  };

  // Confirm delete - delete via API
  const handleConfirmDelete = async () => {
    if (!deletingRecordId) return;

    try {
      setLoading(true);
      const response = await axios.delete(deleteEmailsData(deletingRecordId));

      if (response.data.success) {
        setSuccessMessage('Record deleted successfully');
        setTimeout(() => setSuccessMessage(''), 3000);
        setShowDeleteModal(false);
        setDeletingRecordId(null);
        // Refresh records
        fetchCsvRecords();
      } else {
        setErrorMessage(response.data.message || 'Failed to delete record');
        setTimeout(() => setErrorMessage(''), 3000);
      }
    } catch (error) {
      console.error('Error deleting record:', error);
      setErrorMessage('Error deleting record');
      setTimeout(() => setErrorMessage(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  // Open send email modal
  const handleOpenSendEmailModal = async () => {
    try {
      const response = await axios.get(fromEmails);
      setFromEmailsList(response.data.data || []);
      if (response.data && response.data.length > 0) {
        setEmailFormData(prev => ({
          ...prev,
          fromEmail: response.data[0].email || response.data[0].id || ''
        }));
      }
      // Preserve attributes from existing message if any
      if (emailFormData.message) {
        preservedTableAttributesRef.current = preserveTableAttributes(emailFormData.message);
        originalHtmlWithAttributesRef.current = emailFormData.message;
      }
      setShowSendEmailModal(true);
    } catch (error) {
      console.error('Error fetching from emails:', error);
      setErrorMessage('Error loading from emails');
      setTimeout(() => setErrorMessage(''), 3000);
    }
  };

  // Handle send email form submit
  const handleSendEmail = async (e) => {
    e.preventDefault();

    // Get the message with attributes preserved
    const messageWithAttributes = getMessageWithAttributes();
    
    // Check if message has content (not just empty HTML tags)
    const messageContent = messageWithAttributes.replace(/<[^>]*>/g, '').trim();
    if (!messageContent) {
      setErrorMessage('Please enter a message');
      setTimeout(() => setErrorMessage(''), 3000);
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(sendVlsEmail, {
        fromEmail: emailFormData.fromEmail,
        subject: emailFormData.subject,
        message: messageWithAttributes, // Use version with attributes
        recipients: csvRecords.map(record => record.emails)
      });

      if (response.data.success) {
        setSuccessMessage('Email sent successfully');
        setTimeout(() => setSuccessMessage(''), 3000);
        setShowSendEmailModal(false);
        setEmailFormData({ fromEmail: '', subject: '', message: '' });
        // Refresh records
        fetchCsvRecords();
      } else {
        setErrorMessage(response.data.message || 'Failed to send email');
        setTimeout(() => setErrorMessage(''), 3000);
      }
    } catch (error) {
      console.error('Error sending email:', error);
      setErrorMessage('Error sending email');
      setTimeout(() => setErrorMessage(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  // Custom upload adapter for base64 image embedding (no API call)
  const uploadAdapterPlugin = (editor) => {
    try {
      // Check if FileRepository plugin is available
      const fileRepository = editor.plugins.get('FileRepository');
      if (!fileRepository) {
        console.error('FileRepository plugin not found. Image upload may not work.');
        return;
      }

      // Create custom upload adapter
      fileRepository.createUploadAdapter = (loader) => {
        return new (class {
          constructor(loader) {
            this.loader = loader;
          }

          upload() {
            return this.loader.file.then(file => {
              // Validate file size (60KB = 60 * 1024 bytes)
              const maxSize = 60 * 1024; // 60KB in bytes
              if (file.size > maxSize) {
                const errorMsg = `Image size must be less than 60KB. Current size: ${(file.size / 1024).toFixed(2)}KB`;
                setErrorMessage(errorMsg);
                setTimeout(() => setErrorMessage(''), 5000);
                return Promise.reject(new Error(errorMsg));
              }

              // Validate file type
              if (!file.type.match(/^image\/(jpeg|jpg|png|gif|webp)$/)) {
                const errorMsg = 'Invalid image format. Please use JPEG, PNG, GIF, or WebP';
                setErrorMessage(errorMsg);
                setTimeout(() => setErrorMessage(''), 5000);
                return Promise.reject(new Error(errorMsg));
              }

              // Convert image to base64 data URL (direct embedding in content)
              return new Promise((resolve, reject) => {
                const reader = new FileReader();
                
                reader.onload = () => {
                  // Resolve with base64 data URL - image will be embedded directly in HTML
                  resolve({
                    default: reader.result
                  });
                  setSuccessMessage('Image added successfully');
                  setTimeout(() => setSuccessMessage(''), 3000);
                };
                
                reader.onerror = () => {
                  const errorMsg = 'Error reading image file';
                  setErrorMessage(errorMsg);
                  setTimeout(() => setErrorMessage(''), 5000);
                  reject(new Error(errorMsg));
                };
                
                reader.readAsDataURL(file);
              });
            });
          }

          abort() {
            // Handle upload cancellation if needed
          }
        })(loader);
      };
      console.log('Upload adapter successfully configured');
    } catch (error) {
      console.error('Error setting up upload adapter:', error);
      setErrorMessage('Image upload feature not available. Please check browser console for details.');
      setTimeout(() => setErrorMessage(''), 5000);
    }
  };

  // CKEditor configuration
  const editorConfiguration = {
    toolbar: [
      'heading',
      '|',
      'bold',
      'italic',
      'underline',
      'strikethrough',
      '|',
      'bulletedList',
      'numberedList',
      '|',
      'outdent',
      'indent',
      '|',
      'link',
      'image',
      '|',
      'blockQuote',
      'insertTable',
      '|',
      'undo',
      'redo'
    ],
    image: {
      toolbar: [
        'imageTextAlternative',
        'toggleImageCaption',
        'imageStyle:inline',
        'imageStyle:block',
        'imageStyle:side'
      ],
      upload: {
        types: ['jpeg', 'jpg', 'png', 'gif', 'webp']
      },
      // Enable image insert via URL and upload
      insert: {
        integrations: ['upload', 'url']
      }
    }
  };

  // Utility function to extract and preserve table attributes
  const preserveTableAttributes = (html) => {
    if (!html || typeof html !== 'string') {
      return {};
    }
    
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const tables = doc.querySelectorAll('table');
      const preserved = {};
      
      tables.forEach((table, index) => {
        const key = `table_${index}`;
        preserved[key] = {
          border: table.getAttribute('border') || '',
          cellpadding: table.getAttribute('cellpadding') || '',
          cellspacing: table.getAttribute('cellspacing') || '',
          width: table.getAttribute('width') || '',
          align: table.getAttribute('align') || '',
          style: table.getAttribute('style') || '',
          class: table.getAttribute('class') || ''
        };
      });
      
      return preserved;
    } catch (error) {
      console.error('Error preserving table attributes:', error);
      return {};
    }
  };

  // Utility function to restore table attributes
  const restoreTableAttributes = (html, preservedAttributes) => {
    if (!html || typeof html !== 'string') {
      return html;
    }
    
    if (!preservedAttributes || Object.keys(preservedAttributes).length === 0) {
      return html;
    }

    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const tables = doc.querySelectorAll('table');
      
      // Restore attributes for each table
      tables.forEach((table, index) => {
        const key = `table_${index}`;
        const attrs = preservedAttributes[key];
        
        if (attrs) {
          // Only set attributes that have values
          if (attrs.border !== '') table.setAttribute('border', attrs.border);
          if (attrs.cellpadding !== '') table.setAttribute('cellpadding', attrs.cellpadding);
          if (attrs.cellspacing !== '') table.setAttribute('cellspacing', attrs.cellspacing);
          if (attrs.width !== '') table.setAttribute('width', attrs.width);
          if (attrs.align !== '') table.setAttribute('align', attrs.align);
          if (attrs.style !== '') table.setAttribute('style', attrs.style);
          if (attrs.class !== '') table.setAttribute('class', attrs.class);
        }
      });
      
      return doc.body.innerHTML;
    } catch (error) {
      console.error('Error restoring table attributes:', error);
      return html;
    }
  };

  // Get the current message with attributes preserved
  const getMessageWithAttributes = () => {
    if (isSourceView) {
      // In source mode, use sourceCode which has attributes
      return sourceCode;
    } else {
      // In visual mode, try to restore attributes from preserved data
      if (editorRef.current) {
        const data = editorRef.current.getData();
        const restored = restoreTableAttributes(data, preservedTableAttributesRef.current);
        return restored;
      }
      return emailFormData.message;
    }
  };

  // Source code toggle handler
  const toggleSourceView = () => {
    if (isSourceView) {
      // Switching from source to visual
      if (editorRef.current) {
        // Store original HTML with attributes
        originalHtmlWithAttributesRef.current = sourceCode;
        // Preserve attributes from source code BEFORE setting in editor
        preservedTableAttributesRef.current = preserveTableAttributes(sourceCode);
        // Set the data - CKEditor will strip attributes, but we've preserved them
        editorRef.current.setData(sourceCode);
        // Store the version WITH attributes in emailFormData
        setEmailFormData(prev => ({ ...prev, message: sourceCode }));
      }
      setIsSourceView(false);
    } else {
      // Switching from visual to source
      if (editorRef.current) {
        const html = editorRef.current.getData();
        // Restore attributes from preserved data
        const htmlWithAttributes = restoreTableAttributes(html, preservedTableAttributesRef.current);
        // Update preserved attributes
        preservedTableAttributesRef.current = preserveTableAttributes(htmlWithAttributes);
        setSourceCode(htmlWithAttributes);
        // Also update emailFormData with the version that has attributes
        setEmailFormData(prev => ({ ...prev, message: htmlWithAttributes }));
      }
      setIsSourceView(true);
    }
  };

  // Fullscreen toggle handler
  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  // Handle source code textarea change
  const handleSourceCodeChange = (e) => {
    const newSourceCode = e.target.value;
    // Always preserve table attributes when editing in source mode
    const preserved = preserveTableAttributes(newSourceCode);
    preservedTableAttributesRef.current = preserved;
    originalHtmlWithAttributesRef.current = newSourceCode;
    setSourceCode(newSourceCode);
    // Always store the version WITH attributes in emailFormData
    setEmailFormData(prev => ({ ...prev, message: newSourceCode }));
  };

  return (
    <Layout>
      {successMessage && (
        <div className="alert alert-success alert-dismissible fade show" role="alert">
          {successMessage}
          <button type="button" className="btn-close" onClick={() => setSuccessMessage('')}></button>
        </div>
      )}
      {errorMessage && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          {errorMessage}
          <button type="button" className="btn-close" onClick={() => setErrorMessage('')}></button>
        </div>
      )}

      <section className="w-100 p-3">
        <div style={{ padding: '14px' }}>
          {/* Import CSV Section */}
          <div style={{ backgroundColor: '#1e3a8a', padding: '20px', borderRadius: '8px', marginBottom: '20px' }}>
            <h3 style={{ color: '#fff', marginBottom: '15px' }}>Import CSV Files Here</h3>
            <h5 style={{ color: '#fff', marginBottom: '20px' }}>Import File</h5>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => document.getElementById('csvFileInput').click()}
                style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
              >
                <i className="fa fa-file"></i> Choose File
              </button>
              <input
                type="file"
                id="csvFileInput"
                accept=".csv"
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
              {selectedFile && (
                <span style={{ color: '#fff', alignSelf: 'center' }}>{selectedFile.name}</span>
              )}
              <button
                type="button"
                className="btn btn-success"
                onClick={handleDownloadTemplate}
                style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
              >
                <i className="fa fa-download"></i>
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleUploadCsv}
                disabled={loading || !selectedFile}
                style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
              >
                {loading ? 'Uploading...' : 'Upload CSV File'}
              </button>
            </div>
          </div>

          {/* CSV Records Table */}
          <div style={{ backgroundColor: '#fff', borderRadius: '8px', overflow: 'hidden' }}>
            <div style={{ backgroundColor: '#1e3a8a', padding: '15px' }}>
              <h4 style={{ color: '#fff', margin: 0 }}>All Names, Emails Imported List</h4>
            </div>
            <div className="table-responsive">
              <table className="table table-bordered table-hover mb-0">
                <thead style={{ backgroundColor: '#f8f9fa' }}>
                  <tr>
                    <th>#</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && csvRecords.length === 0 ? (
                    <tr>
                      <td colSpan="4" className="text-center">Loading...</td>
                    </tr>
                  ) : csvRecords.length === 0 ? (
                    <tr>
                      <td colSpan="4" className="text-center">No records found</td>
                    </tr>
                  ) : (
                    csvRecords.map((record, index) => (
                      <tr key={record.id || index}>
                        <td>{index + 1}</td>
                        <td>{record.names || 'N/A'}</td>
                        <td>{record.emails || 'N/A'}</td>
                        <td>
                          <div style={{ display: 'flex', gap: '5px' }}>
                            <button
                              className="btn btn-sm btn-primary"
                              onClick={() => handleEdit(record)}
                            >
                              <i className="fa fa-pencil"></i>
                            </button>
                            <button
                              className="btn btn-sm btn-danger"
                              onClick={() => handleDelete(record.emails_data_id || record.emails_data_id || record.ID)}
                            >
                              <i className="fa fa-trash"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {csvRecords.length > 0 && (
              <div style={{ padding: '15px', textAlign: 'center' }}>
                <button
                  type="button"
                  className="btn btn-primary btn-lg"
                  onClick={handleOpenSendEmailModal}
                  disabled={loading}
                >
                  <i className="fa fa-envelope" style={{ marginRight: '8px' }}></i>
                  Send Email
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Edit Record Modal */}
      {showEditModal && (
        <div
          className="modal fade show"
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}
          tabIndex="-1"
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Edit Record</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingRecord(null);
                    setEditFormData({ names: '', emails: '' });
                  }}
                ></button>
              </div>
              <form onSubmit={handleSaveEdit}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label htmlFor="editName" className="form-label">Name</label>
                    <input
                      type="text"
                      className="form-control"
                      id="editName"
                      value={editFormData.names}
                      onChange={(e) => setEditFormData({ ...editFormData, names: e.target.value })}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label htmlFor="editEmail" className="form-label">Email</label>
                    <input
                      type="email"
                      className="form-control"
                      id="editEmail"
                      value={editFormData.emails}
                      onChange={(e) => setEditFormData({ ...editFormData, emails: e.target.value })}
                      required
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowEditModal(false);
                      setEditingRecord(null);
                      setEditFormData({ names: '', emails: '' });
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading}
                  >
                    {loading ? 'Updating...' : 'Update'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div
          className="modal fade show"
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}
          tabIndex="-1"
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirm Delete</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowDeleteModal(false);
                    setDeletingRecordId(null);
                  }}
                ></button>
              </div>
              <div className="modal-body">
                <p>Are you sure you want to delete this record? This action cannot be undone.</p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowDeleteModal(false);
                    setDeletingRecordId(null);
                  }}
                >
                  No
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleConfirmDelete}
                  disabled={loading}
                >
                  {loading ? 'Deleting...' : 'Yes, Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Send Email Modal */}
      {showSendEmailModal && (
        <div
          className="modal fade show"
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}
          tabIndex="-1"
        >
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content" style={{ borderRadius: '0' }}>
              <div className="modal-header" style={{ backgroundColor: '#1e3a8a', color: '#fff', borderBottom: 'none', padding: '15px 20px' }}>
                <h5 className="modal-title" style={{ margin: 0, fontWeight: 'bold' }}>Email CSV Compose</h5>
                <button
                  type="button"
                  className="btn-close btn-close-black"
                  style={{ backgroundColor: '#e9e9e9', borderRadius: '50%', width: '25px', height: '25px', opacity: '1', padding: '0' }}
                  onClick={() => {
                    setShowSendEmailModal(false);
                    setEmailFormData({ fromEmail: '', subject: '', message: '' });
                  }}
                ></button>
              </div>
              <form onSubmit={handleSendEmail}>
                <div className="modal-body" style={{ padding: '20px' }}>
                  <div className="mb-3">
                    <label htmlFor="fromEmail" className="form-label" style={{ fontWeight: '500', marginBottom: '8px' }}>Email Address</label>
                    <select
                      className="form-select"
                      id="fromEmail"
                      value={emailFormData.fromEmail}
                      onChange={(e) => setEmailFormData({ ...emailFormData, fromEmail: e.target.value })}
                      required
                      style={{ padding: '8px 12px' }}
                    >
                      <option value="">Select Email Address</option>
                      {fromEmailsList.map((email, index) => (
                        <option key={index} value={email.email || email.id || email}>
                          {email.email || email.name || email}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="mb-3">
                    <label htmlFor="subject" className="form-label" style={{ fontWeight: '500', marginBottom: '8px' }}>Email Subject</label>
                    <input
                      type="text"
                      className="form-control"
                      id="subject"
                      value={emailFormData.subject}
                      onChange={(e) => setEmailFormData({ ...emailFormData, subject: e.target.value })}
                      required
                      style={{ padding: '8px 12px' }}
                    />
                  </div>
                  <div className="mb-3" style={{ position: 'relative' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      <label htmlFor="message" className="form-label" style={{ fontWeight: '500', marginBottom: 0 }}>Email Message Body (Upload image size less than 60Kb)</label>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          type="button"
                          className={`btn btn-sm ${isSourceView ? 'btn-primary' : 'btn-outline-secondary'}`}
                          onClick={toggleSourceView}
                          style={{ fontSize: '12px', padding: '4px 8px' }}
                          title={isSourceView ? "Switch to Visual Editor" : "Switch to Source Code"}
                        >
                          <i className="fa fa-code" style={{ marginRight: '4px' }}></i>
                          {isSourceView ? 'Visual' : 'Source'}
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          onClick={toggleFullscreen}
                          style={{ fontSize: '12px', padding: '4px 8px' }}
                          title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
                        >
                          <i className={`fa ${isFullscreen ? 'fa-compress' : 'fa-expand'}`} style={{ marginRight: '4px' }}></i>
                          {isFullscreen ? 'Exit' : 'Fullscreen'}
                        </button>
                      </div>
                    </div>
                    <div
                      style={{
                        position: isFullscreen ? 'fixed' : 'relative',
                        top: isFullscreen ? 0 : 'auto',
                        left: isFullscreen ? 0 : 'auto',
                        right: isFullscreen ? 0 : 'auto',
                        bottom: isFullscreen ? 0 : 'auto',
                        width: isFullscreen ? '100vw' : '100%',
                        height: isFullscreen ? '100vh' : 'auto',
                        zIndex: isFullscreen ? 9999 : 'auto',
                        backgroundColor: isFullscreen ? '#fff' : 'transparent',
                        padding: isFullscreen ? '20px' : '0',
                        overflow: isFullscreen ? 'auto' : 'visible'
                      }}
                    >
                      {isSourceView ? (
                        <textarea
                          ref={sourceTextareaRef}
                          className="form-control"
                          value={sourceCode}
                          onChange={handleSourceCodeChange}
                          style={{
                            minHeight: isFullscreen ? 'calc(100vh - 100px)' : '300px',
                            fontFamily: 'monospace',
                            fontSize: '12px',
                            width: '100%'
                          }}
                          placeholder="Paste your HTML source code here..."
                        />
                      ) : (
                        <div style={{ minHeight: isFullscreen ? 'calc(100vh - 3000px)' : '300px' }}>
                          <style>
                            {`
                              .ck-editor__editable {
                                color: #000000 !important;
                              }
                              .ck-editor__editable p,
                              .ck-editor__editable div,
                              .ck-editor__editable span,
                              .ck-editor__editable li,
                              .ck-editor__editable td,
                              .ck-editor__editable th {
                                color: #000000 !important;
                              }
                              .ck.ck-editor__main>.ck-editor__editable:not(.ck-focused) {
                                height: 300px;
                                border-color: var(--ck-color-base-border);
                              }
                              .ck-content {
                                height: 300px;
                              }
                            `}
                          </style>
                          <CKEditor
                            editor={ClassicEditor}
                            config={editorConfiguration}
                            data={emailFormData.message}
                            onReady={(editor) => {
                              editorRef.current = editor;
                              
                              // Debug: Check available plugins
                              console.log('Available CKEditor plugins:', Object.keys(editor.plugins._plugins));
                              
                              // Check if image plugin is available
                              try {
                                const imagePlugin = editor.plugins.get('Image');
                                console.log('Image plugin found:', imagePlugin);
                              } catch (e) {
                                console.error('Image plugin not found:', e);
                              }
                              
                              // Inject custom upload adapter for base64 image embedding (no API call)
                              uploadAdapterPlugin(editor);
                              
                              // Preserve table attributes when editor is ready
                              // Get the initial data that was passed to the editor (before CKEditor processed it)
                              const initialData = emailFormData.message || '';
                              if (initialData) {
                                // Preserve attributes from the original data (before CKEditor stripped them)
                                preservedTableAttributesRef.current = preserveTableAttributes(initialData);
                                originalHtmlWithAttributesRef.current = initialData;
                                
                                // If the initial data has attributes, restore them in the editor
                                const dataFromEditor = editor.getData();
                                const restoredData = restoreTableAttributes(dataFromEditor, preservedTableAttributesRef.current);
                                if (restoredData !== dataFromEditor && restoredData === initialData) {
                                  // Only update if we have a better version with attributes
                                  editor.setData(restoredData);
                                }
                              }
                            }}
                            onChange={(event, editor) => {
                              const data = editor.getData();
                              
                              // If we're in source view, don't update from editor (use sourceCode instead)
                              if (isSourceView) {
                                return;
                              }
                              
                              // Always restore preserved table attributes before storing
                              let dataWithAttributes = restoreTableAttributes(data, preservedTableAttributesRef.current);
                              
                              // If restoration didn't add attributes, try using original HTML
                              if (dataWithAttributes === data && originalHtmlWithAttributesRef.current) {
                                // Check if we have original HTML with attributes that might match
                                const originalPreserved = preserveTableAttributes(originalHtmlWithAttributesRef.current);
                                if (Object.keys(originalPreserved).length > 0) {
                                  // Try restoring from original
                                  dataWithAttributes = restoreTableAttributes(data, originalPreserved);
                                  // Update preserved attributes
                                  preservedTableAttributesRef.current = originalPreserved;
                                }
                              }
                              
                              // If we still don't have attributes and we have sourceCode with attributes, use that
                              if (dataWithAttributes === data && sourceCode && sourceCode !== data) {
                                const sourcePreserved = preserveTableAttributes(sourceCode);
                                if (Object.keys(sourcePreserved).length > 0) {
                                  dataWithAttributes = restoreTableAttributes(data, sourcePreserved);
                                  preservedTableAttributesRef.current = sourcePreserved;
                                }
                              }
                              
                              // Update preserved attributes for any new tables
                              const currentPreserved = preserveTableAttributes(dataWithAttributes);
                              // Merge: keep existing preserved attributes, add new ones
                              Object.keys(currentPreserved).forEach(key => {
                                if (preservedTableAttributesRef.current[key]) {
                                  // Keep existing preserved attributes, but update if table structure changed
                                  const existing = preservedTableAttributesRef.current[key];
                                  preservedTableAttributesRef.current[key] = { ...existing, ...currentPreserved[key] };
                                } else {
                                  // New table, preserve its attributes
                                  preservedTableAttributesRef.current[key] = currentPreserved[key];
                                }
                              });
                              
                              // Always store the version WITH attributes in emailFormData
                              // This ensures emailFormData.message always has attributes when they exist
                              setEmailFormData(prev => ({ ...prev, message: dataWithAttributes }));
                            }}
                          />
                        </div>
                      )}
                      {isFullscreen && (
                        <div style={{
                          position: 'fixed',
                          top: '10px',
                          right: '10px',
                          zIndex: 10000,
                          display: 'flex',
                          gap: '8px'
                        }}>
                          <button
                            type="button"
                            className={`btn btn-sm ${isSourceView ? 'btn-primary' : 'btn-outline-secondary'}`}
                            onClick={toggleSourceView}
                            style={{ fontSize: '12px', padding: '4px 8px' }}
                          >
                            <i className="fa fa-code" style={{ marginRight: '4px' }}></i>
                            {isSourceView ? 'Visual' : 'Source'}
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            onClick={toggleFullscreen}
                            style={{ fontSize: '12px', padding: '4px 8px' }}
                          >
                            <i className="fa fa-compress" style={{ marginRight: '4px' }}></i>
                            Exit
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="modal-footer" style={{ borderTop: '1px solid #dee2e6', padding: '15px 20px', justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    className="btn"
                    style={{ backgroundColor: '#dc3545', color: '#fff', marginRight: '10px', padding: '8px 20px' }}
                    onClick={() => {
                      setShowSendEmailModal(false);
                      setEmailFormData({ fromEmail: '', subject: '', message: '' });
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn"
                    style={{ backgroundColor: '#28a745', color: '#fff', padding: '8px 20px' }}
                    disabled={loading}
                  >
                    {loading ? 'Sending...' : 'Send Emails'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

    </Layout>
  );
}

export default VlsEmails;
